import { useState, useCallback } from 'react';
import { useSignUp, useUser } from '@clerk/clerk-react';

import { markCodeSent } from '@/components/verification/verificationSendFlag';

/**
 * Best-effort: kick off the deferred email verification (NEA-5773) right after
 * sign-up so the first code is already waiting when the user reaches the
 * dashboard. The Unify Clerk app doesn't verify at sign-up, so the new user's
 * primary email is unverified — `prepareVerification` emails them a code. This
 * is fire-and-forget; if it fails (e.g. the user resource isn't ready yet), the
 * dashboard's VerifyEmailDialog auto-sends on open instead. The session flag
 * stops the dialog double-sending when this succeeds.
 */
async function triggerEmailVerification() {
	try {
		await window.Clerk?.user?.reload();
		const emailAddress = window.Clerk?.user?.primaryEmailAddress;
		if (!emailAddress || emailAddress.verification?.status === 'verified') return;
		await emailAddress.prepareVerification({ strategy: 'email_code' });
		markCodeSent(emailAddress.emailAddress);
	} catch {
		// Swallowed — the dashboard banner/dialog is the fallback path.
	}
}

export const useAuthentication = () => {
	const { signUp, isLoaded: isSignUpLoaded, setActive } = useSignUp();
	const { isSignedIn } = useUser();

	const [isAuthenticating, setIsAuthenticating] = useState(false);
	const [authError, setAuthError] = useState(null);

	const handleEmailPasswordSignUp = useCallback(
		async (email, password, fullName) => {
			if (!isSignUpLoaded) return { success: false };

			try {
				setIsAuthenticating(true);
				setAuthError(null);

				// Split full name into first and last name
				const nameParts = fullName.trim().split(' ');
				const firstName = nameParts[0] || '';
				const lastName = nameParts.slice(1).join(' ') || '';

				// Create the sign-up
				const result = await signUp.create({
					emailAddress: email,
					password,
					firstName,
					lastName,
					unsafeMetadata: {
						product: 'unify'
					}
				});

				// The Unify Clerk app doesn't require email verification at
				// sign-up, so a password sign-up completes immediately and Clerk
				// returns a session — activate it so the user is logged in and
				// onboarding continues inline as that user (NEA-5767). If Clerk
				// returns an incomplete sign-up (e.g. verification got re-enabled)
				// there's no session to activate, so surface it rather than
				// silently leaving the user signed out.
				if (result.status !== 'complete') {
					setIsAuthenticating(false);
					setAuthError('Something went wrong creating your account. Please try again or contact support@near.st.');
					return { success: false, error: `Unexpected sign-up status: ${result.status}`, userId: result.createdUserId };
				}

				await setActive({ session: result.createdSessionId });

				// Fire-and-forget — don't block sign-up on the verification email.
				triggerEmailVerification();

				setIsAuthenticating(false);
				return { success: true, userId: result.createdUserId };
			} catch (err) {
				console.error('Email/password sign-up error:', err);
				setIsAuthenticating(false);

				// Handle specific Clerk errors
				if (err.errors?.[0]?.code === 'form_identifier_exists') {
					setAuthError('This email is already registered. Please sign in instead.');
				} else if (err.errors?.[0]?.code === 'form_password_pwned') {
					setAuthError('This password has been found in a data breach. Please choose a different password.');
				} else if (err.errors?.[0]?.code === 'form_password_length_too_short') {
					setAuthError('Password must be at least 8 characters.');
				} else if (err.errors?.[0]?.message) {
					setAuthError(err.errors[0].message);
				} else {
					setAuthError('Sign-up failed. Please try again.');
				}

				return { success: false, error: err.errors?.[0]?.message || 'Sign-up failed' };
			}
		},
		[signUp, isSignUpLoaded, setActive]
	);

	const clearAuthError = useCallback(() => {
		setAuthError(null);
	}, []);

	return {
		// State
		isSignedIn,
		isAuthenticating,
		authError,

		// Methods

		handleEmailPasswordSignUp,
		clearAuthError
	};
};
